import React from 'react';
import { Modal } from 'react-bootstrap';
import i18n from '../../../i18n';
import './AiAssistantModal.scss';

import { useRef, useCallback } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import SimpleLoader from '../../../components/loaders/SimpleLoader';
import acApi from '../../../axios/ac/acApi';
import { ConversationResponse, ConversationMessage } from '../../../types/rest/blazeMeterApiResponse';
import { getErrorMessage } from '../../../utils/miscUtil';
import { useSelector, useDispatch } from 'react-redux';
import { ApplicationState } from '../../../store/rootReducer';
import { setConversationId, clearConversationId } from '../../../store/aiConversations/aiConversationsActions';
import BzmIcon from '../../../components/icons/BzmIcon';
import MixpanelService from '../../../utils/mixpanelService';
import {
  AI_ASSISTANT_OPENED,
  AI_ASSISTANT_QUESTION_SENT,
  AI_ASSISTANT_SUGGESTION_USED,
  AI_ASSISTANT_RATE_LIMIT_HIT,
} from '../../../utils/mixpanelEvents';
import {
  URL_SUGGESTION_CARDS,
  RESPONSE_BODY_POOL,
  RESPONSE_HEADER_POOL,
  pickNRandom,
  resolveBodyCards,
  resolveFieldCards,
} from './aiAssistantSuggestionPools';

interface AiAssistantModalProps {
  onClose: () => void;
  updateFunction?: (value: string) => void;
  currentValue?: string;
  callerId: string;
  title?: string;
  fieldName?: string;
  matcherType?: string;
  selectionRange?: { start: number; end: number };
}

export const isAppendField = (callerId: string): boolean =>
  callerId.startsWith('qa-input-url') ||
  callerId === 'qa-textarea-body-content' ||
  callerId.startsWith('qa-body-content');

export const isReplaceSelectionField = (callerId: string): boolean =>
  callerId.startsWith('qa-input-url') ||
  callerId === 'qa-textarea-body-content' ||
  callerId.startsWith('qa-body-content');

const MAX_QUESTION_LENGTH = 1500;

const AiAssistantModal: React.FC<AiAssistantModalProps> = props => {
  const dispatch = useDispatch();
  const conversationCache = useSelector((state: ApplicationState) => state.aiConversations.conversationCache);
  const conversationContainerRef = useRef<HTMLDivElement>(null);

  // Get existing conversation ID from Redux cache using the callerId
  const cachedConversationId = conversationCache?.[props.callerId] || null;

  const [questionText, setQuestionText] = React.useState('');
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isLoadingConversation, setIsLoadingConversation] = React.useState<boolean>(false);
  const [conversationHistory, setConversationHistory] = React.useState<
    Array<{ question: string; explanation?: string; suggestion?: string; isError?: boolean }>
  >([]);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [showSuggestions, setShowSuggestions] = React.useState<boolean>(true);

  // Pick 4 random cards on mount — field-specific pools take priority
  const [suggestionCards] = React.useState(() => {
    if (props.callerId.startsWith('qa-input-url')) {
      return pickNRandom(URL_SUGGESTION_CARDS, 4);
    }
    if (props.callerId === 'qa-textarea-body-content') {
      return pickNRandom(RESPONSE_BODY_POOL, 4);
    }
    if (props.callerId.startsWith('qa-body-content')) {
      return resolveBodyCards(props.matcherType);
    }
    if (props.callerId.startsWith('qa-response-value-')) {
      return pickNRandom(RESPONSE_HEADER_POOL, 4);
    }
    return resolveFieldCards(props.callerId, props.fieldName) ?? [];
  });

  // Track AI Assistant opened
  React.useEffect(() => {
    MixpanelService.track(AI_ASSISTANT_OPENED(props.title || 'AI Assistant'));
  }, []);

  // Load previous conversation when modal opens with existing conversationId
  React.useEffect(() => {
    const loadPreviousConversation = async () => {
      if (cachedConversationId) {
        try {
          setIsLoadingConversation(true);
          const response = await acApi.aiAssistant.getConversation(cachedConversationId);
          const data = response.data;

          if (data && data.result) {
            const result: ConversationResponse = data.result;
            if (result.messages && result.messages.length > 0) {
              // Convert messages to conversation history format
              const history: Array<{ question: string; explanation?: string; suggestion?: string }> = [];

              // Group messages by pairs (user question -> AI answer)
              for (let i = 0; i < result.messages.length - 1; i += 2) {
                const userMessage = result.messages[i];
                const aiMessage = result.messages[i + 1];

                if (userMessage.role === 'user' && aiMessage.role === 'model') {
                  history.push({
                    question: userMessage.text || '',
                    explanation: aiMessage.explanation,
                    suggestion: aiMessage.suggestion,
                  });
                }
              }

              setConversationHistory(history);

              // Hide suggestions if there's existing conversation
              if (history.length > 0) {
                setShowSuggestions(false);
              }
            }
          }
        } catch (e) {
          const msg = getErrorMessage(e);
          console.error('Failed to load previous conversation:', msg);
          // Continue with empty conversation - don't show error to user
        } finally {
          setIsLoadingConversation(false);
        }
      }
    };

    loadPreviousConversation();
  }, [cachedConversationId]); // Depend on cachedConversationId

  // Auto-scroll to bottom when conversation history changes
  React.useEffect(() => {
    if (conversationContainerRef.current) {
      conversationContainerRef.current.scrollTop = conversationContainerRef.current.scrollHeight;
    }
  }, [conversationHistory]);

  const handleClose = useCallback(() => {
    props.onClose();
  }, [props]);

  const handleAppendSuggestion = useCallback(
    (suggestion: string) => {
      MixpanelService.track(AI_ASSISTANT_SUGGESTION_USED('append', props.title || 'AI Assistant'));
      if (props.updateFunction) {
        const currentValue = props.currentValue || '';
        const combinedValue = currentValue ? `${currentValue}${suggestion}` : suggestion;
        props.updateFunction(combinedValue);
      }
      handleClose();
    },
    [props.updateFunction, props.currentValue, props.title, handleClose],
  );

  const handleOverrideSuggestion = useCallback(
    (suggestion: string) => {
      MixpanelService.track(AI_ASSISTANT_SUGGESTION_USED('override', props.title || 'AI Assistant'));
      if (props.updateFunction) {
        props.updateFunction(suggestion);
      }
      handleClose();
    },
    [props.updateFunction, props.title, handleClose],
  );

  const handleReplaceSelection = useCallback(
    (suggestion: string) => {
      MixpanelService.track(AI_ASSISTANT_SUGGESTION_USED('replace-selection', props.title || 'AI Assistant'));
      if (props.updateFunction && props.selectionRange) {
        const current = props.currentValue || '';
        const { start, end } = props.selectionRange;
        props.updateFunction(current.slice(0, start) + suggestion + current.slice(end));
      }
      handleClose();
    },
    [props.updateFunction, props.currentValue, props.selectionRange, props.title, handleClose],
  );

  const handleCopySuggestion = useCallback(
    (suggestion: string) => {
      MixpanelService.track(AI_ASSISTANT_SUGGESTION_USED('copy', props.title || 'AI Assistant'));
      navigator.clipboard.writeText(suggestion);
    },
    [props.title],
  );

  const handleSuggestionCardClick = useCallback((text: string) => {
    setQuestionText(text);
    setShowSuggestions(false);
  }, []);

  const handleDismissSuggestions = useCallback(() => {
    setShowSuggestions(false);
  }, []);

  const handleResetConversation = useCallback(
    (e?: React.MouseEvent) => {
      e?.preventDefault();

      // Clear cached conversation id so the next request starts a brand new conversation.
      dispatch(clearConversationId(props.callerId));

      // Reset local UI to a blank state.
      setConversationHistory([]);
      setQuestionText('');
      setErrorMessage(null);
      setIsLoading(false);
      setIsLoadingConversation(false);
      setShowSuggestions(true); // Show suggestions again when reset
    },
    [dispatch, props.callerId],
  );

  const callAi = async () => {
    // Validate question length before calling API
    if (questionText.length > MAX_QUESTION_LENGTH) {
      setErrorMessage(`Question is too long. Maximum allowed length is ${MAX_QUESTION_LENGTH} characters.`);
      return;
    }

    setIsLoading(true);
    setShowSuggestions(false); // Hide suggestions when user sends a message
    const currentQuestion = questionText;
    MixpanelService.track(AI_ASSISTANT_QUESTION_SENT(props.title || 'AI Assistant'));
    try {
      const response = await acApi.aiAssistant.getExpressionSuggestion(
        currentQuestion,
        cachedConversationId || undefined,
      );
      const data = response.data;
      if (data && data.result) {
        const result: ConversationResponse = data.result;

        // Store the conversation ID in Redux using default key
        if (result.conversationId) {
          dispatch(setConversationId(props.callerId, result.conversationId));
        }

        // Extract the AI's suggestion from the messages
        if (result.messages && result.messages.length > 0) {
          const lastMessage: ConversationMessage = result.messages[result.messages.length - 1];
          if (lastMessage.role === 'model') {
            // Add to conversation history with explanation and suggestion
            setConversationHistory(prev => [
              ...prev,
              {
                question: currentQuestion,
                explanation: lastMessage.explanation,
                suggestion: lastMessage.suggestion,
              },
            ]);

            // Clear the input field for next question
            setQuestionText('');
            setErrorMessage(null);
          }
        }
      }
    } catch (e) {
      const msg = getErrorMessage(e);
      console.error('AI call failed:', msg);
      if (msg.startsWith('Too many requests')) {
        MixpanelService.track(AI_ASSISTANT_RATE_LIMIT_HIT(props.title || 'AI Assistant'));
      }
      if (msg.startsWith('I can help you generate Regex')) {
        setConversationHistory(prev => [
          ...prev,
          {
            question: currentQuestion,
            explanation: msg,
          },
        ]);
        setQuestionText('');
        setErrorMessage(null);
      } else {
        setErrorMessage(msg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault(); // Prevent adding a new line
      if (!isLoading && !isLoadingConversation && questionText.trim()) {
        callAi();
      }
    }
    // Allow Shift+Enter for new lines
  };

  return (
    <>
      <Modal show={true} onHide={handleClose} className="ai-assistant-modal" enforceFocus={false}>
        <div className="modal-header">
          <div className="d-flex flex-column flex-grow-1">
            <h5 className="modal-title" id="qa-clone-service-modal-title">
              <BzmIcon variant={'icon-bzm-ai-stars'} className={'mr-1 text-primary'} /> {props.title || 'AI Assistant'}
            </h5>
            {conversationHistory.length > 0 && (
              <Button
                variant="link"
                onClick={handleResetConversation}
                className="p-0 small mt-2 reset-conversation-btn"
                style={{ display: 'inline-flex', alignItems: 'center', width: 'fit-content', borderLeftWidth: '10px' }}
              >
                <span className="reset-icon">
                  <BzmIcon variant={'icon-bzm-refresh'} className={'mr-1'} />
                </span>
                <span className="reset-text">Reset Conversation</span>
              </Button>
            )}
          </div>
          <Button
            variant="link"
            className="close"
            aria-label={i18n.t('common:actions.close')}
            id={'qa-clone-service-modal-btn-close-icon'}
            title={i18n.t('common:actions.close')}
            onClick={handleClose}
            style={{ alignSelf: 'flex-start', paddingRight: '30px' }}
          >
            <span aria-hidden="true">&times;</span>
          </Button>
        </div>
        <Modal.Body id={'qa-clone-modal-body-text'}>
          {isLoadingConversation ? (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
              <SimpleLoader loadingText="Loading previous conversation..." />
            </div>
          ) : (
            <div className="ai-conversation-container" ref={conversationContainerRef}>
              {/* Suggestion Cards - Show only when showSuggestions is true and no conversation history */}
              {showSuggestions && conversationHistory.length === 0 && suggestionCards.length > 0 && (
                <div className="ai-suggestions-wrapper">
                  <div className="ai-suggestions-header">
                    <span>Smart suggestions for you to try</span>
                    {/*                    <Button variant="link" onClick={handleDismissSuggestions} className="ai-dismiss-suggestions btn-sm">
                      ✕
                    </Button>*/}
                  </div>
                  <div className="ai-suggestion-cards">
                    {suggestionCards.map((card, index) => (
                      <div
                        key={index}
                        role="button"
                        tabIndex={0}
                        className="ai-suggestion-card"
                        onClick={() => handleSuggestionCardClick(card.text)}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleSuggestionCardClick(card.text);
                          }
                        }}
                      >
                        <BzmIcon variant={'icon-bzm-ai-stars'} className={'mr-1 text-primary'} />
                        <div className="ai-suggestion-card-content">
                          <div className="ai-suggestion-card-title">{card.title}</div>
                          <div className="ai-suggestion-card-text">{card.text}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Display Conversation History */}
              {conversationHistory.map((item, index) => (
                <div key={index} className="ai-conversation-item">
                  {/* User Question */}
                  <div className="ai-user-question">
                    <div className="ai-question-content">{item.question}</div>
                  </div>

                  {/* AI Response Group - includes both response and suggestion */}
                  <div className="ai-response-group">
                    {/* AI Response - Only show if explanation exists */}
                    {item.explanation && (
                      <div className="ai-response-section">
                        <div className="ai-response-content">{item.explanation}</div>
                      </div>
                    )}

                    {/* AI Suggestion - Only show if suggestion exists */}
                    {item.suggestion && (
                      <div className="ai-suggestion-section">
                        {!item.isError && <div className="ai-suggestion-label">AI Suggestion</div>}
                        <div className="ai-suggestion-content" style={item.isError ? { color: '#d11323' } : undefined}>
                          {item.suggestion}
                        </div>
                        {!item.isError && (
                          <div className="ai-suggestion-actions">
                            {(() => {
                              const showAppend = isAppendField(props.callerId);
                              const hasSelection = !!(
                                props.selectionRange && props.selectionRange.start !== props.selectionRange.end
                              );
                              const showReplaceSelection = isReplaceSelectionField(props.callerId) && hasSelection;
                              const overrideLabel = showReplaceSelection
                                ? 'Replace Selection'
                                : showAppend
                                ? 'Override'
                                : 'Apply';
                              return (
                                <>
                                  {showAppend && (
                                    <Button
                                      id={`append-suggestion-btn-${index}`}
                                      variant="primary"
                                      onClick={() => handleAppendSuggestion(item.suggestion || '')}
                                      className="ai-suggestion-btn"
                                    >
                                      Append
                                    </Button>
                                  )}
                                  <Button
                                    id={`override-suggestion-btn-${index}`}
                                    variant={showAppend ? 'secondary' : 'primary'}
                                    onClick={() =>
                                      showReplaceSelection
                                        ? handleReplaceSelection(item.suggestion || '')
                                        : handleOverrideSuggestion(item.suggestion || '')
                                    }
                                    className="ai-suggestion-btn"
                                  >
                                    {overrideLabel}
                                  </Button>
                                  <Button
                                    id={`copy-suggestion-btn-${index}`}
                                    variant="secondary"
                                    onClick={() => handleCopySuggestion(item.suggestion || '')}
                                    className="ai-suggestion-btn"
                                  >
                                    Copy
                                  </Button>
                                </>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {/* Show loader for new AI calls */}
              {isLoading && (
                <div className="ai-conversation-item">
                  <div className="d-flex justify-content-start align-items-center py-4">
                    <SimpleLoader loadingText="Waiting for the response..." spinnerFirst />
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal.Body>

        {/* Fixed User Input Section - Always at bottom */}
        <div className="ai-input-section">
          <div className="ai-input-wrapper">
            <Form.Control
              as="textarea"
              id="qa-textarea-user-input"
              rows={3}
              name="question"
              placeholder="Ask AI"
              value={questionText}
              onChange={e => {
                const value = e.currentTarget.value;
                setQuestionText(value);

                // Validate length and show error if too long
                if (value.length > MAX_QUESTION_LENGTH) {
                  setErrorMessage(`Question is too long. Maximum allowed length is ${MAX_QUESTION_LENGTH} characters.`);
                } else {
                  setErrorMessage(null);
                }
              }}
              onKeyDown={handleKeyDown}
              className="ai-input-field"
              disabled={isLoading || isLoadingConversation}
            />
            <Button
              id={'send-question-btn'}
              variant="primary"
              onClick={() => {
                if (
                  !isLoading &&
                  !isLoadingConversation &&
                  questionText.trim() &&
                  questionText.length <= MAX_QUESTION_LENGTH
                ) {
                  setErrorMessage(null);
                  callAi();
                }
              }}
              disabled={
                isLoading || isLoadingConversation || !questionText.trim() || questionText.length > MAX_QUESTION_LENGTH
              }
              className="ai-send-button"
            >
              {isLoading ? 'Waiting for the response...' : 'Send'}
            </Button>
          </div>
          <div className="ai-action-row">
            {errorMessage && (
              <div className="ai-error-text" role="status" aria-live="polite">
                {errorMessage}
              </div>
            )}
          </div>
          <div className="ai-disclaimer-section">
            <span className="text-muted">AI can make mistakes. Verify important information.</span>
          </div>
        </div>
        <Modal.Footer className="flex-column align-items-end"></Modal.Footer>
      </Modal>
    </>
  );
};

export default AiAssistantModal;
